import { Box, BoxProps, useToken } from "@chakra-ui/react";
import { Resizable, ResizableProps, ResizeCallback } from "re-resizable";
import React, { ReactNode } from "react";

import { SIZES } from "@nens/ranalib";
import { hexToRgb } from "@nens/shared-utils";

type ResizableSidebarProps = {
  side: "left" | "right";
  children: ReactNode;
  topOffset?: string;
  defaultWidth?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  boxProps?: BoxProps;
  onResizeStop?: ResizeCallback;
};

/**
 * A resizable sidebar container for map controls.
 *
 * **Important**: When wrapping ControlsLeft or ControlsRight components, always pass `w="full"`
 * to those components. This signals them to skip their own background styling to prevent
 * double-stacking of semi-transparent backgrounds, which would make the sidebar appear darker.
 */

export const SIDEBAR_DEFAULT_WIDTH = SIZES.sm;
export const ResizableSidebar = ({
  side,
  children,
  topOffset = SIZES[16],
  defaultWidth = SIDEBAR_DEFAULT_WIDTH,
  minWidth = 0,
  maxWidth = SIZES.lg,
  boxProps,
  onResizeStop,
}: ResizableSidebarProps) => {
  const [docked] = useToken("zIndices", ["docked"]);
  const [grey100] = useToken("colors", ["brand.grey.100"]);
  const grey100RGB = hexToRgb(grey100);
  const resizeHandle = (
    <Box
      position="absolute"
      top="1/2"
      right={side === "right" ? "1/2" : undefined}
      left={side === "left" ? "1/2" : undefined}
      w={3}
      h={11}
      borderRadius="full"
      bg="brand.border"
      roundedLeft={side === "left" ? "none" : undefined}
      roundedRight={side === "right" ? "none" : undefined}
    >
      <Box
        position="absolute"
        inset="0"
        m="auto"
        w="1"
        borderLeftWidth="thin"
        borderRightWidth="thin"
        h="1/2"
        rounded="none"
        borderColor="brand.grey.100"
      />
    </Box>
  );

  const resizableProps: Partial<ResizableProps> = {
    defaultSize: { width: defaultWidth, height: "100%" },
    minWidth,
    maxWidth,
    enable: side === "left" ? { right: true } : { left: true },
    handleComponent: side === "left" ? { right: resizeHandle } : { left: resizeHandle },

    style: { pointerEvents: "all", height: "100%" },
    handleStyles: { left: { zIndex: docked }, right: { zIndex: docked } },
    onResizeStop,
  };

  return (
    <Box
      position="absolute"
      borderColor="brand.border"
      {...(side === "left"
        ? { left: 0, borderRightWidth: "base" }
        : { right: 0, borderLeftWidth: "base" })}
      zIndex="docked"
      pointerEvents="none"
      bg={`rgba(${grey100RGB}, 0.9)`}
      backdropFilter="blur(5px)"
      maxH={`calc(100% - ${topOffset})`}
      h="full"
      top={topOffset}
      {...boxProps}
    >
      <Resizable {...resizableProps}>{children}</Resizable>
    </Box>
  );
};
